import { motion } from 'framer-motion';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ConsultationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    availability: '',
    currentStatus: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5p33hn1urhk7g0fu240', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          availability: '',
          currentStatus: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-3 bg-amber-100 px-6 py-3 rounded-full mb-8">
                <i className="ri-chat-3-line text-2xl text-amber-600 w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
                <span className="text-amber-800 font-bold text-lg">気軽に相談できる場所</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-black text-stone-800 mb-6 leading-tight">
                吉野なんでも相談所
              </h1>

              <p className="text-2xl md:text-3xl text-stone-700 mb-6 font-bold">
                「ちょっと気になる」から<br className="md:hidden" />「本気で移住したい」まで
              </p>

              <p className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-2xl mx-auto">
                実際に吉野で活動しているメンバーが、リアルな情報をお伝えします。<br />
                完璧な計画は不要。まずは気軽にご相談ください。
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-3 gap-8 mb-20"
            >
              <div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-amber-500">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-amber-600 w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold text-stone-800 mb-3">返信目安: 2-3営業日</h3>
                <p className="text-stone-600 leading-relaxed">
                  相談内容に応じて、いちばん合う人につなぎます
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-amber-500">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-checkbox-circle-line text-2xl text-amber-600 w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold text-stone-800 mb-3">決まってなくてOK</h3>
                <p className="text-stone-600 leading-relaxed">
                  「何から始めればいいか分からない」という相談も大歓迎
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-amber-500">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-user-heart-line text-2xl text-amber-600 w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
                </div>
                <h3 className="text-xl font-bold text-stone-800 mb-3">現地メンバーが対応</h3>
                <p className="text-stone-600 leading-relaxed">
                  実際に吉野で活動している人から、リアルな話が聞けます
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
                <h2 className="text-3xl font-black text-stone-800 mb-4 text-center">
                  相談フォーム
                </h2>
                <p className="text-stone-600 text-center mb-10 leading-relaxed">
                  長文いりません。「気になるテーマ・動けそうな時期・いまの状態」だけ教えてください。
                </p>

                <form id="consultation-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-stone-700 font-bold mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                      placeholder="山田太郎"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-stone-700 font-bold mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                      placeholder="example@email.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-stone-700 font-bold mb-2">
                      電話番号 <span className="text-stone-400 text-sm font-normal">(任意)</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  {/* Interest */}
                  <div>
                    <label htmlFor="interest" className="block text-stone-700 font-bold mb-2">
                      気になるテーマ <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="移住・定住">移住・定住</option>
                      <option value="古民家リノベーション">古民家リノベーション</option>
                      <option value="農業・林業">農業・林業</option>
                      <option value="起業・事業">起業・事業</option>
                      <option value="地域おこし協力隊">地域おこし協力隊</option>
                      <option value="短期滞在・体験">短期滞在・体験</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  {/* Availability */}
                  <div>
                    <label htmlFor="availability" className="block text-stone-700 font-bold mb-2">
                      動けそうな時期 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="availability"
                      name="availability"
                      value={formData.availability}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="すぐにでも">すぐにでも</option>
                      <option value="1-3ヶ月以内">1-3ヶ月以内</option>
                      <option value="3-6ヶ月以内">3-6ヶ月以内</option>
                      <option value="半年〜1年後">半年〜1年後</option>
                      <option value="1年以上先">1年以上先</option>
                      <option value="まだ未定">まだ未定</option>
                    </select>
                  </div>

                  {/* Current Status */}
                  <div>
                    <label htmlFor="currentStatus" className="block text-stone-700 font-bold mb-2">
                      いまの状態 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="currentStatus"
                      name="currentStatus"
                      value={formData.currentStatus}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="ちょっと気になる程度">ちょっと気になる程度</option>
                      <option value="情報収集中">情報収集中</option>
                      <option value="具体的に検討中">具体的に検討中</option>
                      <option value="ほぼ決めている">ほぼ決めている</option>
                      <option value="すでに動き始めている">すでに動き始めている</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-stone-700 font-bold mb-2">
                      相談内容・質問など <span className="text-stone-400 text-sm font-normal">(任意)</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base resize-none"
                      placeholder="気になることや質問があれば、お気軽にどうぞ（500文字以内）"
                    />
                    <p className="text-sm text-stone-500 mt-2 text-right">
                      {formData.message.length} / 500文字
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-black py-4 px-8 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-3">
                        <i className="ri-loader-4-line animate-spin text-xl w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                        送信中...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-3">
                        <i className="ri-send-plane-fill text-xl w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                        相談を送信する
                      </span>
                    )}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg"
                    >
                      <div className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-2xl text-green-600 w-6 h-6 flex items-center justify-center flex-shrink-0" aria-hidden="true"></i>
                        <div>
                          <p className="font-bold mb-1">送信完了しました！</p>
                          <p className="text-sm">2-3営業日以内に、担当者よりご連絡いたします。</p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg"
                    >
                      <div className="flex items-start gap-3">
                        <i className="ri-error-warning-fill text-2xl text-red-600 w-6 h-6 flex items-center justify-center flex-shrink-0" aria-hidden="true"></i>
                        <div>
                          <p className="font-bold mb-1">送信に失敗しました</p>
                          <p className="text-sm">もう一度お試しいただくか、直接メールでご連絡ください。</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 bg-stone-50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-black text-stone-800 mb-12 text-center">
                よくある質問
              </h2>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-bold text-stone-800 mb-3 flex items-start gap-3">
                    <i className="ri-question-line text-amber-600 text-xl w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                    何も決まっていなくても相談できますか？
                  </h3>
                  <p className="text-stone-600 leading-relaxed pl-8">
                    はい、大歓迎です。「ちょっと気になる」という段階でも、お気軽にご相談ください。むしろ、早い段階でリアルな情報を知ることで、より良い選択ができます。
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-bold text-stone-800 mb-3 flex items-start gap-3">
                    <i className="ri-question-line text-amber-600 text-xl w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                    相談したら必ず参加しないといけませんか？
                  </h3>
                  <p className="text-stone-600 leading-relaxed pl-8">
                    いいえ、そんなことはありません。情報収集のための相談も大歓迎です。実際に話を聞いてみて、「やっぱり違うな」と思ったら、それも大切な判断です。
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-bold text-stone-800 mb-3 flex items-start gap-3">
                    <i className="ri-question-line text-amber-600 text-xl w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                    どんな人が対応してくれますか？
                  </h3>
                  <p className="text-stone-600 leading-relaxed pl-8">
                    実際に吉野で活動しているメンバーが対応します。移住者、地元の方、起業した方など、相談内容に応じて最適な人をご紹介します。
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-bold text-stone-800 mb-3 flex items-start gap-3">
                    <i className="ri-question-line text-amber-600 text-xl w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                    オンラインでの相談も可能ですか？
                  </h3>
                  <p className="text-stone-600 leading-relaxed pl-8">
                    はい、可能です。まずはオンラインで気軽にお話しして、興味が深まったら現地訪問という流れもおすすめです。毎週水曜19:00〜のオンライン雑談会もあります。
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-black text-stone-800 mb-6">
                まずは気軽に、<br className="md:hidden" />一歩踏み出してみませんか？
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed mb-10">
                完璧な計画より、「気になる」という気持ち。<br />
                それが、挑戦の第一歩です。
              </p>
              <a
                href="#consultation-form"
                className="inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-600 text-white font-black px-10 py-4 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-up-line text-xl w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                フォームに戻る
              </a>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default ConsultationPage;
